import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ReceiptDialog } from "../components/receipts/ReceiptDialog";
import { formatCurrency, formatDateTime } from "../lib/utils";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import toast from "react-hot-toast";
import axios from "axios";
import { Receipt, DollarSign, CreditCard, Printer, Plus, Trash2, Package, Search, X } from "lucide-react";

const API = `${window.location.origin}/api`;

export default function SalesPage() {
  const { } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [navList, setNavList] = useState([]); // ✨ snapshot للقائمة وقت فتح الفاتورة (للتنقل الثابت)
  const [shopInfo, setShopInfo] = useState(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // نموذج إضافة مبيعات
  const [saleItems, setSaleItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [itemQuantity, setItemQuantity] = useState("");
  const [itemPrice, setItemPrice] = useState("");
  const [manualProductName, setManualProductName] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [manualCustomerName, setManualCustomerName] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [inputMode, setInputMode] = useState("select"); // select or manual
  // ✨ خيارات نوع الدفع المتقدمة + حقل الملاحظات
  const [creditMode, setCreditMode] = useState("credit_period"); // credit_period | installment | partial
  const [installmentsCount, setInstallmentsCount] = useState(3);
  const [installmentPeriodDays, setInstallmentPeriodDays] = useState(30);
  const [protectionLevel, setProtectionLevel] = useState("medium");
  const [saleNotes, setSaleNotes] = useState("");
  // ✨ تاريخ مخصص للفاتورة (للفواتير القديمة)
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [customInvoiceDate, setCustomInvoiceDate] = useState("");
  // ✨ تعديل تاريخ فاتورة موجودة
  const [editDateSale, setEditDateSale] = useState(null);
  const [editDateValue, setEditDateValue] = useState("");
  // ✨ تعديل بيانات الفاتورة (مستوى 1 - آمن)
  const [editMetaSale, setEditMetaSale] = useState(null);
  const [editMetaForm, setEditMetaForm] = useState({ customer_name: "", customer_phone: "", notes: "", due_date: "" });
  // ✨ إصدار إشعار تصحيح (Credit Note)
  const [correctionSale, setCorrectionSale] = useState(null);
  const [correctionForm, setCorrectionForm] = useState({ direction: "decrease", amount: "", reason: "", adjust_debt: true });

  // Multi-field search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchCustomer, setSearchCustomer] = useState(""); // اسم أو هاتف
  const [searchInvoice, setSearchInvoice] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [searchAmount, setSearchAmount] = useState("");
  // ✨ فلتر نوع الدفع: all | cash | credit | credit_period | installment | partial
  const [searchPaymentFilter, setSearchPaymentFilter] = useState("all");

  const clearSearch = () => {
    setSearchCustomer(""); setSearchInvoice(""); setSearchDate(""); setSearchAmount("");
    setSearchPaymentFilter("all");
  };

  const filteredSales = sales.filter((s) => {
    if (searchCustomer.trim()) {
      const q = searchCustomer.trim().toLowerCase();
      const name = (s.customer_name || "").toLowerCase();
      const phone = (s.customer_phone || "").toLowerCase();
      // البحث بالاسم أو الهاتف (تطابق جزئي)
      if (!name.includes(q) && !phone.includes(q)) return false;
    }
    if (searchInvoice.trim()) {
      const inv = (s.invoice_number || "").toLowerCase();
      if (!inv.includes(searchInvoice.trim().toLowerCase())) return false;
    }
    if (searchDate) {
      const saleDate = (s.created_at || "").slice(0, 10);
      if (saleDate !== searchDate) return false;
    }
    if (searchAmount.trim()) {
      const amount = Number(searchAmount.trim());
      if (!isNaN(amount)) {
        // ±5% tolerance للمبلغ
        const tolerance = Math.max(amount * 0.05, 1);
        if (Math.abs((s.total || 0) - amount) > tolerance) return false;
      }
    }
    // ✨ فلتر نوع الدفع
    if (searchPaymentFilter !== "all") {
      const pt = s.payment_type;
      const cm = s.credit_mode;
      if (searchPaymentFilter === "cash") {
        if (pt !== "cash") return false;
      } else if (searchPaymentFilter === "credit") {
        if (pt !== "credit") return false;
      } else if (searchPaymentFilter === "credit_period") {
        if (pt !== "credit" || (cm && cm !== "credit_period")) return false;
        // إذا cm فاضي اعتبره آجل بفترة افتراضياً
      } else if (searchPaymentFilter === "installment") {
        if (pt !== "credit" || cm !== "installment") return false;
      } else if (searchPaymentFilter === "partial") {
        if (pt !== "credit" || cm !== "partial") return false;
      }
    }
    return true;
  });

  const hasActiveSearch = searchCustomer || searchInvoice || searchDate || searchAmount || searchPaymentFilter !== "all";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [salesRes, settingsRes, productsRes, customersRes] = await Promise.all([
        axios.get(`${API}/sales`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/products`),
        axios.get(`${API}/customers`),
      ]);
      setSales(salesRes.data);
      setProducts(productsRes.data);
      setCustomers(customersRes.data);
      setShopInfo({
        name: settingsRes.data.shop_name,
        phone: settingsRes.data.phone,
        whatsapp: settingsRes.data.whatsapp_number,
      });
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const openReceipt = (sale) => {
    // ✨ نحفظ snapshot من القائمة الحالية وقت فتح الفاتورة
    // علشان التنقل بـ السابق/التالي يكون ثابت ولا يتأثر بالفلترة أو إعادة التحميل
    const currentList = filteredSales && filteredSales.length > 0 ? [...filteredSales] : [...sales];
    setNavList(currentList);
    setSelectedSale(sale);
    setIsReceiptOpen(true);
  };

  const addItem = () => {
    let productName = "";
    let productId = null;
    let price = parseFloat(itemPrice) || 0;
    
    if (inputMode === "select") {
      if (!selectedProduct) {
        toast.error(language === "ar" ? "اختر منتج" : "Select a product");
        return;
      }
      const product = products.find(p => p.id === selectedProduct);
      productName = product.name;
      productId = product.id;
      price = price || product.sale_price;
    } else {
      if (!manualProductName) {
        toast.error(language === "ar" ? "أدخل اسم المنتج" : "Enter product name");
        return;
      }
      productName = manualProductName;
    }
    
    if (!itemQuantity || parseInt(itemQuantity) <= 0) {
      toast.error(language === "ar" ? "أدخل الكمية" : "Enter quantity");
      return;
    }
    
    if (!price || price <= 0) {
      toast.error(language === "ar" ? "أدخل السعر" : "Enter price");
      return;
    }
    
    const item = {
      product_id: productId,
      product_name: productName,
      quantity: parseInt(itemQuantity),
      price: price,
      total: parseInt(itemQuantity) * price,
    };
    setSaleItems([...saleItems, item]);
    setSelectedProduct("");
    setManualProductName("");
    setItemQuantity("");
    setItemPrice("");
  };

  const removeItem = (index) => {
    setSaleItems(saleItems.filter((_, i) => i !== index));
  };

  const saleTotal = saleItems.reduce((sum, item) => sum + item.total, 0);

  const handleSubmit = async () => {
    if (saleItems.length === 0) {
      toast.error(language === "ar" ? "أضف منتجات" : "Add products");
      return;
    }

    try {
      const customerName = selectedCustomer 
        ? customers.find(c => c.id === selectedCustomer)?.name 
        : manualCustomerName || null;
      
      const saleData = {
        items: saleItems,
        total: saleTotal,
        payment_type: paymentType,
        customer_id: selectedCustomer || null,
        customer_name: customerName,
        amount_paid: paymentType === "cash" ? saleTotal : parseFloat(amountPaid) || 0,
        due_date: paymentType === "credit" && dueDate ? dueDate : null,
        // ✨ خيارات الدفع المتقدمة + ملاحظات
        credit_mode: paymentType === "credit" ? creditMode : null,
        installments_count: paymentType === "credit" && creditMode === "installment" ? installmentsCount : null,
        installment_period_days: paymentType === "credit" && creditMode === "installment" ? installmentPeriodDays : null,
        protection_level: protectionLevel,
        notes: saleNotes.trim() || null,
        // ✨ تاريخ مخصص (إذا اختار المستخدم تاريخ قديم)
        invoice_date: useCustomDate && customInvoiceDate
          ? new Date(customInvoiceDate).toISOString()
          : null,
      };
      
      const response = await axios.post(`${API}/sales`, saleData);
      const isOfflinePending = response.data?._offline_pending === true;
      
      if (isOfflinePending) {
        toast.success(language === "ar" ? "💾 تم الحفظ محلياً — سيُرسل عند رجوع الإنترنت" : "💾 Saved locally — will sync online");
      } else {
        toast.success(language === "ar" ? "تم تسجيل المبيعات" : "Sale recorded");
        // ✨ تنبيه ربط الأقساط
        if (response.data?.linked_installment_id) {
          toast.success(
            language === "ar"
              ? "🔗 الفاتورة رُبطت بصفحة 'بيع بالتقسيط' تلقائياً"
              : "🔗 Auto-linked to installments page",
            { duration: 5000, icon: "📅" }
          );
        }
        // فتح الوصل فقط لو الفاتورة تم حفظها فعلياً (online)
        setSelectedSale(response.data);
        setIsReceiptOpen(true);
      }
      
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSaleItems([]);
    setSelectedProduct("");
    setManualProductName("");
    setItemQuantity("");
    setItemPrice("");
    setPaymentType("cash");
    setSelectedCustomer("");
    setManualCustomerName("");
    setAmountPaid("");
    setDueDate("");
    setInputMode("select");
    setCreditMode("credit_period");
    setInstallmentsCount(3);
    setInstallmentPeriodDays(30);
    setProtectionLevel("medium");
    setSaleNotes("");
    setUseCustomDate(false);
    setCustomInvoiceDate("");
  };

  const handleProductSelect = (productId) => {
    setSelectedProduct(productId);
    const product = products.find(p => p.id === productId);
    if (product) {
      setItemPrice(product.sale_price.toString());
    }
  };

  const totalSales = sales.reduce((sum, s) => sum + s.total, 0);
  const cashSales = sales.filter(s => s.payment_type === "cash").reduce((sum, s) => sum + s.total, 0);
  const creditSales = sales.filter(s => s.payment_type === "credit").reduce((sum, s) => sum + s.total, 0);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="sales-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-white">{t("sales")}</h1>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setSearchOpen((v) => !v)}
            variant="outline"
            className={`border-slate-600 text-slate-200 hover:bg-slate-700 ${searchOpen || hasActiveSearch ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : ""}`}
            data-testid="toggle-sales-search-btn"
          >
            <Search className="w-4 h-4 me-2" />
            {language === "ar" ? "بحث في الفواتير" : "Search Invoices"}
            {hasActiveSearch && (
              <span className="ms-2 bg-emerald-500 text-white text-[10px] px-1.5 py-0.5 rounded-full" data-testid="active-search-badge">
                {filteredSales.length}
              </span>
            )}
          </Button>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 btn-lift"
            data-testid="add-sale-btn"
          >
            <Plus className="w-4 h-4 me-2" />
            {language === "ar" ? "إضافة مبيعات" : "Add Sale"}
          </Button>
        </div>
      </div>

      {/* Multi-field Search Bar */}
      {searchOpen && (
        <Card className="bg-slate-800/60 border-emerald-500/30 animate-fade-in-up">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-emerald-400 font-bold flex items-center gap-2">
                <Search className="w-4 h-4" />
                {language === "ar" ? "بحث متعدد المعايير" : "Multi-criteria Search"}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { clearSearch(); setSearchOpen(false); }}
                className="h-7 w-7 text-slate-400 hover:text-white"
                data-testid="close-sales-search-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-slate-300 text-xs mb-1 block">{language === "ar" ? "اسم العميل أو الهاتف" : "Customer name or phone"}</Label>
                <Input
                  value={searchCustomer}
                  onChange={(e) => setSearchCustomer(e.target.value)}
                  placeholder={language === "ar" ? "ابحث بالاسم أو الهاتف..." : "Search by name or phone..."}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="search-customer-input"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1 block">{language === "ar" ? "رقم الفاتورة" : "Invoice #"}</Label>
                <Input
                  value={searchInvoice}
                  onChange={(e) => setSearchInvoice(e.target.value)}
                  placeholder="INV-..."
                  className="bg-slate-900 border-slate-700 text-white font-mono"
                  data-testid="search-invoice-input"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1 block">{language === "ar" ? "التاريخ" : "Date"}</Label>
                <Input
                  type="date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="search-date-input"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs mb-1 block">{language === "ar" ? "المبلغ (±5%)" : "Amount (±5%)"}</Label>
                <Input
                  type="number"
                  value={searchAmount}
                  onChange={(e) => setSearchAmount(e.target.value)}
                  placeholder="0"
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="search-amount-input"
                />
              </div>
            </div>

            {/* ✨ فلتر نوع الدفع */}
            <div className="pt-2 border-t border-slate-700">
              <Label className="text-slate-300 text-xs mb-2 block">
                {language === "ar" ? "نوع الدفع" : "Payment type"}
              </Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "all",           ar: "الكل",        en: "All",          activeCls: "bg-emerald-600 hover:bg-emerald-700 text-white text-xs" },
                  { key: "cash",          ar: "نقدي",        en: "Cash",         activeCls: "bg-green-600 hover:bg-green-700 text-white text-xs" },
                  { key: "credit",        ar: "آجل (الكل)",  en: "Credit (all)", activeCls: "bg-amber-600 hover:bg-amber-700 text-white text-xs" },
                  { key: "credit_period", ar: "آجل بفترة",   en: "Period",       activeCls: "bg-amber-700 hover:bg-amber-800 text-white text-xs" },
                  { key: "installment",   ar: "أقساط",       en: "Installments", activeCls: "bg-blue-600 hover:bg-blue-700 text-white text-xs" },
                  { key: "partial",       ar: "جزئي",        en: "Partial",      activeCls: "bg-purple-600 hover:bg-purple-700 text-white text-xs" },
                ].map((opt) => (
                  <Button
                    key={opt.key}
                    type="button"
                    size="sm"
                    variant={searchPaymentFilter === opt.key ? "default" : "outline"}
                    onClick={() => setSearchPaymentFilter(opt.key)}
                    className={
                      searchPaymentFilter === opt.key
                        ? opt.activeCls
                        : "border-slate-600 text-slate-300 text-xs hover:bg-slate-700"
                    }
                    data-testid={`search-payment-filter-${opt.key}-btn`}
                  >
                    {language === "ar" ? opt.ar : opt.en}
                  </Button>
                ))}
              </div>
            </div>
            {hasActiveSearch && (
              <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                <span className="text-slate-300 text-sm">
                  {language === "ar"
                    ? `النتائج: ${filteredSales.length} من أصل ${sales.length}`
                    : `Results: ${filteredSales.length} of ${sales.length}`}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSearch}
                  className="text-amber-400 hover:bg-amber-500/10"
                  data-testid="clear-search-btn"
                >
                  {language === "ar" ? "مسح المعايير" : "Clear filters"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/10">
              <Receipt className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">{language === "ar" ? "إجمالي المبيعات" : "Total Sales"}</p>
              <p className="text-xl font-bold text-white">{formatCurrency(totalSales)}</p>
              <USDHint amount={totalSales} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <DollarSign className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">{t("cashSales")}</p>
              <p className="text-xl font-bold text-white">{formatCurrency(cashSales)}</p>
              <USDHint amount={cashSales} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-500/10">
              <CreditCard className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">{t("creditSales")}</p>
              <p className="text-xl font-bold text-white">{formatCurrency(creditSales)}</p>
              <USDHint amount={creditSales} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
            </div>
          ) : sales.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">{t("noData")}</p>
            </div>
          ) : filteredSales.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">
                {language === "ar"
                  ? "لا توجد فواتير تطابق معايير البحث"
                  : "No invoices match the search criteria"}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSearch}
                className="mt-3 text-emerald-400 hover:bg-emerald-500/10"
              >
                {language === "ar" ? "مسح المعايير" : "Clear filters"}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-slate-700">
                    <TableHead className="text-amber-500">{language === "ar" ? "رقم الوصل" : "Invoice #"}</TableHead>
                    <TableHead className="text-amber-500">{t("date")}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "المنتجات" : "Items"}</TableHead>
                    <TableHead className="text-amber-500">{t("total")}</TableHead>
                    <TableHead className="text-amber-500">{t("paymentType")}</TableHead>
                    <TableHead className="text-amber-500">{t("customer")}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "الوصل" : "Receipt"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.map((sale) => (
                    <TableRow key={sale.id} className="border-slate-700 hover:bg-slate-800/50">
                      <TableCell className="text-emerald-400 font-mono font-bold">
                        <div className="flex items-center gap-1">
                          <span>{sale.invoice_number || "-"}</span>
                          {(sale.corrections_count || 0) > 0 && (
                            <span
                              title={language === "ar" ? `${sale.corrections_count} إشعار تصحيح` : `${sale.corrections_count} correction(s)`}
                              className="text-amber-400 text-xs bg-amber-900/30 px-1 rounded"
                            >
                              📝{sale.corrections_count}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        <div className="flex items-center gap-2">
                          <span>{formatDateTime(sale.invoice_date || sale.created_at, language === "ar" ? "ar-IQ" : "en-US")}</span>
                          {sale.is_backdated && (
                            <span title={language === "ar" ? "تاريخ مُعدَّل يدوياً" : "Manually backdated"} className="text-amber-400 text-xs">📅</span>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditDateSale(sale);
                              const d = new Date(sale.invoice_date || sale.created_at);
                              const offset = d.getTimezoneOffset();
                              const local = new Date(d.getTime() - offset * 60000);
                              setEditDateValue(local.toISOString().slice(0, 16));
                            }}
                            className="h-6 w-6 text-slate-500 hover:text-emerald-400"
                            title={language === "ar" ? "تعديل التاريخ" : "Edit date"}
                            data-testid={`edit-date-btn-${sale.id}`}
                          >
                            ✏️
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-white">
                        <div className="max-w-xs">
                          {sale.items.map((item, idx) => (
                            <span key={idx} className="text-sm">
                              {item.product_name} ({item.quantity})
                              {idx < sale.items.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-green-500 font-bold">
                        {formatCurrency(sale.total)}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          sale.payment_type === "cash"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-amber-500/20 text-amber-400"
                        }`}>
                          {sale.payment_type === "cash" ? t("cash") : t("credit")}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {sale.customer_name || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openReceipt(sale)}
                            className="h-8 w-8 text-slate-400 hover:text-white"
                            data-testid={`receipt-btn-${sale.id}`}
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditMetaSale(sale);
                              setEditMetaForm({
                                customer_name: sale.customer_name || "",
                                customer_phone: sale.customer_phone || "",
                                notes: sale.notes || "",
                                due_date: sale.due_date || "",
                              });
                            }}
                            className="h-8 w-8 text-slate-400 hover:text-emerald-400"
                            title={language === "ar" ? "تعديل بيانات الفاتورة" : "Edit invoice info"}
                            data-testid={`edit-meta-btn-${sale.id}`}
                          >
                            ✏️
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setCorrectionSale(sale);
                              setCorrectionForm({ direction: "decrease", amount: "", reason: "", adjust_debt: true });
                            }}
                            className="h-8 w-8 text-slate-400 hover:text-amber-400"
                            title={language === "ar" ? "إصدار إشعار تصحيح (دائن)" : "Issue credit note"}
                            data-testid={`correction-btn-${sale.id}`}
                          >
                            📝
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Sale Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {language === "ar" ? "إضافة مبيعات جديدة" : "Add New Sale"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* اختيار طريقة الإدخال */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={inputMode === "select" ? "default" : "outline"}
                onClick={() => setInputMode("select")}
                className={inputMode === "select" ? "bg-emerald-600" : "border-slate-600 text-slate-300"}
                size="sm"
              >
                <Package className="w-4 h-4 me-2" />
                {language === "ar" ? "من المخزون" : "From Inventory"}
              </Button>
              <Button
                type="button"
                variant={inputMode === "manual" ? "default" : "outline"}
                onClick={() => setInputMode("manual")}
                className={inputMode === "manual" ? "bg-emerald-600" : "border-slate-600 text-slate-300"}
                size="sm"
              >
                {language === "ar" ? "إدخال يدوي" : "Manual Entry"}
              </Button>
            </div>

            {/* إضافة منتج */}
            {inputMode === "select" ? (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <Select value={selectedProduct} onValueChange={handleProductSelect}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white col-span-2">
                      <SelectValue placeholder={language === "ar" ? "اختر منتج" : "Select product"} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      {products.filter(p => p.quantity > 0).map((product) => (
                        <SelectItem key={product.id} value={product.id} className="text-white hover:bg-slate-700">
                          {product.name} ({product.quantity})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder={language === "ar" ? "الكمية" : "Qty"}
                    value={itemQuantity}
                    onChange={(e) => setItemQuantity(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <PriceInput
                  value={parseFloat(itemPrice) || 0}
                  onChange={(v) => setItemPrice(String(v))}
                  placeholder={language === "ar" ? "السعر" : "Price"}
                  testId="sales-select-price-input"
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder={language === "ar" ? "اسم المنتج" : "Product name"}
                    value={manualProductName}
                    onChange={(e) => setManualProductName(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white col-span-2"
                  />
                  <Input
                    type="number"
                    placeholder={language === "ar" ? "الكمية" : "Qty"}
                    value={itemQuantity}
                    onChange={(e) => setItemQuantity(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
                <PriceInput
                  value={parseFloat(itemPrice) || 0}
                  onChange={(v) => setItemPrice(String(v))}
                  placeholder={language === "ar" ? "السعر" : "Price"}
                  testId="sales-manual-price-input"
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
            )}

            <Button type="button" onClick={addItem} variant="outline" className="w-full border-slate-600 text-slate-300">
              <Plus className="w-4 h-4 me-2" />
              {language === "ar" ? "إضافة منتج" : "Add Item"}
            </Button>

            {/* Items List */}
            {saleItems.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {saleItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center p-2 rounded bg-slate-900/50">
                    <span className="col-span-4 text-white text-sm truncate" title={item.product_name}>
                      {item.product_name}
                    </span>
                    <div className="col-span-3 flex items-center gap-1">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => {
                          const q = parseInt(e.target.value) || 0;
                          if (q < 1) return;
                          const updated = [...saleItems];
                          updated[index] = { ...item, quantity: q, total: q * item.price };
                          setSaleItems(updated);
                        }}
                        className="bg-slate-800 border-slate-700 text-white h-8 text-sm text-center"
                        data-testid={`item-quantity-${index}`}
                      />
                    </div>
                    <div className="col-span-3 flex items-center gap-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onChange={(e) => {
                          const p = parseFloat(e.target.value) || 0;
                          const updated = [...saleItems];
                          updated[index] = { ...item, price: p, total: item.quantity * p };
                          setSaleItems(updated);
                        }}
                        className="bg-slate-800 border-slate-700 text-white h-8 text-sm text-center"
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                    <span className="col-span-1 text-green-500 text-sm font-bold text-end">
                      {formatCurrency(item.total)}
                    </span>
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeItem(index)} className="col-span-1 text-red-500 h-8 w-8 justify-self-end">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <div className="grid grid-cols-12 gap-2 px-2 text-xs text-slate-500">
                  <span className="col-span-4">{language === "ar" ? "المنتج" : "Item"}</span>
                  <span className="col-span-3 text-center">{language === "ar" ? "الكمية" : "Qty"}</span>
                  <span className="col-span-3 text-center">{language === "ar" ? "السعر" : "Price"}</span>
                  <span className="col-span-1 text-end">{language === "ar" ? "الإجمالي" : "Total"}</span>
                </div>
              </div>
            )}

            {/* Total */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-900 border border-green-500/30">
              <span className="text-green-400">{t("total")}</span>
              <span className="text-white font-bold text-xl">{formatCurrency(saleTotal)}</span>
            </div>

            {/* Payment Type */}
            <div className="space-y-2">
              <Label className="text-slate-200">{t("paymentType")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={paymentType === "cash" ? "default" : "outline"}
                  onClick={() => setPaymentType("cash")}
                  className={paymentType === "cash" ? "bg-green-600" : "border-slate-600 text-slate-300"}
                >
                  <DollarSign className="w-4 h-4 me-2" />
                  {t("cash")}
                </Button>
                <Button
                  type="button"
                  variant={paymentType === "credit" ? "default" : "outline"}
                  onClick={() => setPaymentType("credit")}
                  className={paymentType === "credit" ? "bg-amber-600" : "border-slate-600 text-slate-300"}
                >
                  <CreditCard className="w-4 h-4 me-2" />
                  {t("credit")}
                </Button>
              </div>
            </div>

            {/* Customer Selection for Credit */}
            {paymentType === "credit" && (
              <div className="space-y-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                {/* ✨ نوع الدفع الآجل */}
                <div className="space-y-2">
                  <Label className="text-slate-200 text-sm">{language === "ar" ? "نوع الدفع الآجل" : "Credit type"}</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={creditMode === "credit_period" ? "default" : "outline"}
                      onClick={() => setCreditMode("credit_period")}
                      className={creditMode === "credit_period" ? "bg-amber-600 hover:bg-amber-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                      data-testid="sales-credit-mode-period-btn"
                    >
                      ⏳ {language === "ar" ? "آجل بفترة" : "Period"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={creditMode === "installment" ? "default" : "outline"}
                      onClick={() => setCreditMode("installment")}
                      className={creditMode === "installment" ? "bg-amber-600 hover:bg-amber-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                      data-testid="sales-credit-mode-installment-btn"
                    >
                      📅 {language === "ar" ? "أقساط" : "Installments"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={creditMode === "partial" ? "default" : "outline"}
                      onClick={() => setCreditMode("partial")}
                      className={creditMode === "partial" ? "bg-amber-600 hover:bg-amber-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                      data-testid="sales-credit-mode-partial-btn"
                    >
                      💵 {language === "ar" ? "جزئي" : "Partial"}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-200">{t("customer")}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={selectedCustomer} onValueChange={(v) => { setSelectedCustomer(v); setManualCustomerName(""); }}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                        <SelectValue placeholder={language === "ar" ? "اختر زبون" : "Select customer"} />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id} className="text-white hover:bg-slate-700">
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder={language === "ar" ? "أو اكتب اسم جديد" : "Or enter new name"}
                      value={manualCustomerName}
                      onChange={(e) => { setManualCustomerName(e.target.value); setSelectedCustomer(""); }}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-200">
                      {creditMode === "partial"
                        ? (language === "ar" ? "المبلغ المدفوع جزئياً" : "Partial paid")
                        : t("amountPaid")}
                    </Label>
                    <PriceInput
                      value={parseFloat(amountPaid) || 0}
                      onChange={(v) => setAmountPaid(String(v))}
                      placeholder="0"
                      testId="sales-amount-paid-input"
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200">
                      {creditMode === "installment"
                        ? (language === "ar" ? "تاريخ أول قسط" : "First installment date")
                        : (language === "ar" ? "تاريخ الاستحقاق" : "Due Date")}
                    </Label>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                </div>

                {/* ✨ حقول الأقساط — تظهر فقط عند اختيار "أقساط" */}
                {creditMode === "installment" && (
                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-amber-500/20">
                    <div className="space-y-2">
                      <Label className="text-slate-200 text-sm">{language === "ar" ? "عدد الأقساط" : "Installments"}</Label>
                      <Input
                        type="number"
                        min="1"
                        value={installmentsCount}
                        onChange={(e) => setInstallmentsCount(parseInt(e.target.value) || 1)}
                        className="bg-slate-900 border-slate-700 text-white"
                        data-testid="sales-installments-count-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-200 text-sm">{language === "ar" ? "كل (يوم)" : "Every (days)"}</Label>
                      <Input
                        type="number"
                        min="1"
                        value={installmentPeriodDays}
                        onChange={(e) => setInstallmentPeriodDays(parseInt(e.target.value) || 1)}
                        className="bg-slate-900 border-slate-700 text-white"
                        data-testid="sales-installment-period-input"
                      />
                    </div>
                    <p className="col-span-2 text-[11px] text-amber-200/80">
                      🔗 {language === "ar"
                        ? "ستُربط الفاتورة تلقائياً بصفحة 'بيع بالتقسيط' بعد الحفظ"
                        : "Will auto-link to the Installments page on save"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ✨ مستوى حماية الفاتورة */}
            <div className="space-y-2 p-3 rounded-lg bg-slate-900/60 border border-slate-700">
              <Label className="text-slate-200 flex items-center gap-2 text-sm">
                🛡️ {language === "ar" ? "مستوى حماية الفاتورة" : "Invoice protection"}
              </Label>
              <div className="grid grid-cols-3 gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant={protectionLevel === "weak" ? "default" : "outline"}
                  onClick={() => setProtectionLevel("weak")}
                  className={protectionLevel === "weak" ? "bg-slate-500 hover:bg-slate-600 text-xs" : "border-slate-600 text-slate-400 text-xs"}
                  data-testid="sales-protection-weak-btn"
                >
                  🔓 {language === "ar" ? "ضعيف" : "Weak"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={protectionLevel === "medium" ? "default" : "outline"}
                  onClick={() => setProtectionLevel("medium")}
                  className={protectionLevel === "medium" ? "bg-amber-600 hover:bg-amber-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                  data-testid="sales-protection-medium-btn"
                >
                  🔐 {language === "ar" ? "متوسط" : "Medium"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={protectionLevel === "strong" ? "default" : "outline"}
                  onClick={() => setProtectionLevel("strong")}
                  className={protectionLevel === "strong" ? "bg-emerald-600 hover:bg-emerald-700 text-xs" : "border-slate-600 text-slate-300 text-xs"}
                  data-testid="sales-protection-strong-btn"
                >
                  🛡️ {language === "ar" ? "قوي" : "Strong"}
                </Button>
              </div>
            </div>

            {/* ✨ حقل الملاحظات */}
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                📝 {language === "ar" ? "ملاحظات على الفاتورة (اختياري)" : "Invoice notes (optional)"}
              </Label>
              <textarea
                value={saleNotes}
                onChange={(e) => setSaleNotes(e.target.value)}
                rows={3}
                placeholder={language === "ar"
                  ? "مثال: تسليم خلال 3 أيام، ضمان 6 أشهر، رقم الموديل..."
                  : "e.g. delivery in 3 days, 6-month warranty, model #..."}
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
                data-testid="sales-notes-input"
              />
            </div>

            {/* ✨ تاريخ مخصص للفاتورة (للفواتير القديمة) */}
            <div className="space-y-2 border border-slate-700 rounded-lg p-3 bg-slate-900/40">
              <div className="flex items-center justify-between">
                <Label className="text-slate-200 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useCustomDate}
                    onChange={(e) => {
                      setUseCustomDate(e.target.checked);
                      if (e.target.checked && !customInvoiceDate) {
                        // افتراضياً: اليوم بصيغة datetime-local
                        const now = new Date();
                        const offset = now.getTimezoneOffset();
                        const local = new Date(now.getTime() - offset * 60000);
                        setCustomInvoiceDate(local.toISOString().slice(0, 16));
                      }
                    }}
                    className="w-4 h-4 accent-emerald-500"
                    data-testid="use-custom-date-checkbox"
                  />
                  📅 {language === "ar" ? "تحديد تاريخ مخصص للفاتورة" : "Set custom invoice date"}
                </Label>
              </div>
              {useCustomDate && (
                <div className="space-y-2">
                  <input
                    type="datetime-local"
                    value={customInvoiceDate}
                    onChange={(e) => setCustomInvoiceDate(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                    data-testid="custom-invoice-date-input"
                  />
                  <p className="text-xs text-amber-400/80">
                    ⚠️ {language === "ar"
                      ? "سيُسجَّل التاريخ الحقيقي للإنشاء أيضاً (للتدقيق)"
                      : "Actual creation time will be logged (for audit)"}
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-slate-600 text-slate-300">
              {t("cancel")}
            </Button>
            <Button type="button" onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-sale-btn">
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <ReceiptDialog
        isOpen={isReceiptOpen}
        onClose={() => { setIsReceiptOpen(false); setNavList([]); }}
        data={selectedSale}
        type={selectedSale?.payment_type === "credit" ? "sale_credit" : "sale_cash"}
        language={language}
        allowReturn={true}
        onReturned={() => fetchData()}
        navigation={(() => {
          // ✨ نستخدم navList (snapshot) بدل filteredSales المتغيرة
          if (!selectedSale || !navList || navList.length === 0) return null;
          const idx = navList.findIndex((s) => s.id === selectedSale.id);
          if (idx < 0) return null;
          const goto = (i) => {
            if (i >= 0 && i < navList.length) {
              const target = navList[i];
              // نحاول إيجاد أحدث نسخة من الفاتورة من sales (لو تم تعديلها)
              const fresh = sales.find((s) => s.id === target.id) || target;
              setSelectedSale(fresh);
            }
          };
          return {
            currentIndex: idx,
            total: navList.length,
            onFirst: () => goto(0),
            onPrev: () => goto(idx - 1),
            onNext: () => goto(idx + 1),
            onLast: () => goto(navList.length - 1),
          };
        })()}
      />

      {/* ✨ Dialog: تعديل تاريخ فاتورة موجودة */}
      <Dialog open={!!editDateSale} onOpenChange={(open) => { if (!open) { setEditDateSale(null); setEditDateValue(""); } }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-400">
              📅 {language === "ar" ? "تعديل تاريخ الفاتورة" : "Edit Invoice Date"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-slate-400">
              {language === "ar" ? "رقم الوصل: " : "Invoice #: "}
              <span className="text-emerald-400 font-mono font-bold">{editDateSale?.invoice_number}</span>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">
                {language === "ar" ? "التاريخ والوقت الجديد" : "New date & time"}
              </Label>
              <input
                type="datetime-local"
                value={editDateValue}
                onChange={(e) => setEditDateValue(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                data-testid="edit-invoice-date-input"
              />
            </div>
            <p className="text-xs text-amber-400/80">
              ⚠️ {language === "ar"
                ? "سيتم حفظ تاريخ التعديل للتدقيق الداخلي"
                : "Edit timestamp will be logged for audit"}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setEditDateSale(null); setEditDateValue(""); }} className="border-slate-600 text-slate-300">
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                if (!editDateValue) {
                  toast.error(language === "ar" ? "اختر التاريخ" : "Choose a date");
                  return;
                }
                try {
                  const iso = new Date(editDateValue).toISOString();
                  await axios.patch(`${API}/sales/${editDateSale.id}/date`, { invoice_date: iso });
                  toast.success(language === "ar" ? "✅ تم تعديل تاريخ الفاتورة" : "Invoice date updated");
                  setEditDateSale(null);
                  setEditDateValue("");
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || t("error"));
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-invoice-date-btn"
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ✨ Dialog: تعديل بيانات الفاتورة (آمن - مستوى 1) */}
      <Dialog open={!!editMetaSale} onOpenChange={(open) => { if (!open) setEditMetaSale(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">
              ✏️ {language === "ar" ? "تعديل بيانات الفاتورة" : "Edit Invoice Info"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-sm text-slate-400">
              {language === "ar" ? "رقم الوصل: " : "Invoice #: "}
              <span className="text-emerald-400 font-mono font-bold">{editMetaSale?.invoice_number}</span>
            </div>
            <div className="bg-slate-900/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-400">
              💡 {language === "ar"
                ? "هذا التعديل آمن: لا يلمس المنتجات أو الأموال أو المخزون أو الديون"
                : "Safe edit: items, amounts, stock, and debts remain unchanged"}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "اسم الزبون" : "Customer name"}</Label>
              <Input
                value={editMetaForm.customer_name}
                onChange={(e) => setEditMetaForm({ ...editMetaForm, customer_name: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="edit-meta-customer-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "هاتف الزبون" : "Customer phone"}</Label>
              <Input
                value={editMetaForm.customer_phone}
                onChange={(e) => setEditMetaForm({ ...editMetaForm, customer_phone: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="edit-meta-customer-phone"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "ملاحظات" : "Notes"}</Label>
              <textarea
                value={editMetaForm.notes}
                onChange={(e) => setEditMetaForm({ ...editMetaForm, notes: e.target.value })}
                rows={3}
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
                data-testid="edit-meta-notes"
              />
            </div>
            {editMetaSale?.payment_type === "credit" && (
              <div className="space-y-2">
                <Label className="text-slate-200">{language === "ar" ? "تاريخ الاستحقاق" : "Due date"}</Label>
                <Input
                  type="date"
                  value={editMetaForm.due_date ? editMetaForm.due_date.slice(0, 10) : ""}
                  onChange={(e) => setEditMetaForm({ ...editMetaForm, due_date: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="edit-meta-due-date"
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditMetaSale(null)} className="border-slate-600 text-slate-300">
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                try {
                  await axios.patch(`${API}/sales/${editMetaSale.id}/meta`, {
                    customer_name: editMetaForm.customer_name,
                    customer_phone: editMetaForm.customer_phone,
                    notes: editMetaForm.notes,
                    due_date: editMetaForm.due_date || null,
                  });
                  toast.success(language === "ar" ? "✅ تم تعديل بيانات الفاتورة" : "Invoice info updated");
                  setEditMetaSale(null);
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || t("error"));
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-meta-btn"
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ✨ Dialog: إصدار إشعار تصحيح (Credit Note) */}
      <Dialog open={!!correctionSale} onOpenChange={(open) => { if (!open) setCorrectionSale(null); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-400">
              📝 {language === "ar" ? "إصدار إشعار تصحيح (دائن)" : "Issue Credit Note"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-slate-900/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-400">
              💡 {language === "ar"
                ? "بدلاً من تعديل الفاتورة الأصلية، يُصدر إشعار رسمي يوضح التصحيح. الفاتورة تبقى ثابتة، والتقارير تحسب الفرق تلقائياً."
                : "Instead of editing the original invoice, an official correction note is issued. The original stays intact and reports auto-adjust."}
            </div>
            <div className="text-sm text-slate-400">
              {language === "ar" ? "الفاتورة الأصلية: " : "Original invoice: "}
              <span className="text-emerald-400 font-mono font-bold">{correctionSale?.invoice_number}</span>
              {" — "}
              <span className="text-green-400">{formatCurrency(correctionSale?.total || 0)}</span>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "نوع التصحيح" : "Correction type"}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={correctionForm.direction === "decrease" ? "default" : "outline"}
                  onClick={() => setCorrectionForm({ ...correctionForm, direction: "decrease" })}
                  className={correctionForm.direction === "decrease" ? "bg-rose-600 hover:bg-rose-700" : "border-slate-600 text-slate-300"}
                  data-testid="correction-decrease"
                >
                  ↓ {language === "ar" ? "تخفيض المبلغ" : "Decrease"}
                </Button>
                <Button
                  type="button"
                  variant={correctionForm.direction === "increase" ? "default" : "outline"}
                  onClick={() => setCorrectionForm({ ...correctionForm, direction: "increase" })}
                  className={correctionForm.direction === "increase" ? "bg-emerald-600 hover:bg-emerald-700" : "border-slate-600 text-slate-300"}
                  data-testid="correction-increase"
                >
                  ↑ {language === "ar" ? "زيادة المبلغ" : "Increase"}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "المبلغ" : "Amount"}</Label>
              <Input
                type="number"
                value={correctionForm.amount}
                onChange={(e) => setCorrectionForm({ ...correctionForm, amount: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="0"
                data-testid="correction-amount"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">{language === "ar" ? "السبب (إلزامي)" : "Reason (required)"}</Label>
              <textarea
                value={correctionForm.reason}
                onChange={(e) => setCorrectionForm({ ...correctionForm, reason: e.target.value })}
                rows={3}
                placeholder={language === "ar" ? "مثال: خطأ بالكتابة، خصم لاحق، رجوع منتج..." : "e.g. typo, after-sale discount, item return..."}
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 resize-none"
                data-testid="correction-reason"
              />
            </div>

            {correctionSale?.payment_type === "credit" && correctionSale?.customer_id && (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={correctionForm.adjust_debt}
                  onChange={(e) => setCorrectionForm({ ...correctionForm, adjust_debt: e.target.checked })}
                  className="w-4 h-4 accent-amber-500"
                  data-testid="correction-adjust-debt"
                />
                {language === "ar" ? "تعديل دين الزبون تلقائياً" : "Auto-adjust customer debt"}
              </label>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCorrectionSale(null)} className="border-slate-600 text-slate-300">
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                const amount = parseFloat(correctionForm.amount);
                if (!amount || amount <= 0) {
                  toast.error(language === "ar" ? "أدخل مبلغ صحيح" : "Enter a valid amount");
                  return;
                }
                if (!correctionForm.reason.trim()) {
                  toast.error(language === "ar" ? "أدخل سبب التصحيح" : "Enter reason");
                  return;
                }
                try {
                  const r = await axios.post(`${API}/correction-notes`, {
                    invoice_type: "sale",
                    invoice_id: correctionSale.id,
                    direction: correctionForm.direction,
                    amount,
                    reason: correctionForm.reason.trim(),
                    adjust_debt: correctionForm.adjust_debt,
                  });
                  toast.success(language === "ar" ? `✅ تم إصدار الإشعار ${r.data.note_number}` : `Issued ${r.data.note_number}`);
                  setCorrectionSale(null);
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || t("error"));
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-correction-btn"
            >
              {language === "ar" ? "إصدار الإشعار" : "Issue Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
